import { AriaLivePoliteness, LiveAnnouncer } from '@angular/cdk/a11y';
import {
    ApplicationRef,
    ComponentRef,
    computed,
    createComponent,
    DOCUMENT,
    EnvironmentInjector,
    inject,
    Injectable,
    inputBinding,
    outputBinding,
    Signal,
    signal,
    WritableSignal
} from '@angular/core';
import { ToastType } from '@app/design-system/toast/toast-type';
import { ToastConfiguration } from './toast-configuration';
import { ToastContainer } from './toast-container/toast-container';
import { ToastPayload } from './toast-payload';

@Injectable({
    providedIn: 'root',
})
export class ToastManager {
    private readonly applicationRef: ApplicationRef = inject(ApplicationRef);
    private readonly document: Document = inject(DOCUMENT);
    private readonly environmentInjector: EnvironmentInjector = inject(EnvironmentInjector);

    // Owns a single, persistent (visually-hidden) live region that exists in the DOM before any
    // toast is ever opened — unlike a brand-new `<app-toast>` node, which arrives with its role
    // and text already attached in the same mutation and is unreliably (or not at all) announced
    // by VoiceOver, especially "polite" ones competing with other DOM changes (e.g. a comment
    // list re-rendering). `LiveAnnouncer` clears, then re-sets, this same node's text on every
    // call, which is the standards-based way to reliably trigger an AT announcement.
    private readonly liveAnnouncer: LiveAnnouncer = inject(LiveAnnouncer);

    private readonly toasts: WritableSignal<ToastConfiguration[]> = signal<ToastConfiguration[]>([]);

    private readonly toastPayloads: Signal<readonly ToastPayload[]> = computed((): readonly ToastPayload[] => {
        return this.toasts().map((toastConfiguration: ToastConfiguration): ToastPayload => ({
            id: toastConfiguration.id,
            type: toastConfiguration.type,
            title: toastConfiguration.title,
            message: toastConfiguration.message,
        }))
    })

    public constructor() {
        // Create the host element for the toast container
        const hostElement: HTMLElement = this.document.createElement('app-toast-container');
        this.document.body.appendChild(hostElement);

        // Create the actual container component
        const toastContainerRef: ComponentRef<ToastContainer> = createComponent(
            ToastContainer,
            {
                environmentInjector: this.environmentInjector,
                hostElement,
                bindings: [
                    inputBinding('toasts', this.toastPayloads),
                    outputBinding<string>('closeToast', (id: string): void => this.removeById(id)),
                ],
            },
        );

        // Connect the component to change detection cycle
        this.applicationRef.attachView(toastContainerRef.hostView);
    }

    /**
     * Display an error toast.
     * @param title The title.
     * @param message The message.
     */
    public error(title: string, message?: string): void {
        this.open('error', title, message);
    }

    /**
     * Display an info toast.
     * @param title The title.
     * @param message The message.
     */
    public info(title: string, message?: string): void {
        this.open('info', title, message);
    }

    /**
     * Display a success toast.
     * @param title The title.
     * @param message The message.
     */
    public success(title: string, message?: string): void {
        this.open('success', title, message);
    }

    /**
     * Displays a warning toast
     * @param title The title.
     * @param message The message.
     */
    public warning(title: string, message?: string): void {
        this.open('warning', title, message);
    }

    /**
     * Opens a toast.
     * @param type The type of the toast.
     * @param title The title of the toast.
     * @param message The message of the toast.
     */
    public open(type: ToastType, title: string, message: string = ''): void {
        const toastId: string = crypto.randomUUID();
        const timeoutId: number = setTimeout(
            (): void => this.removeById(toastId),
            5000,
        );
        this.toasts.update((toasts: ToastConfiguration[]): ToastConfiguration[] => {
            return [
                // Insert new toasts at the top
                {
                    id: toastId,
                    timeoutId,
                    type,
                    title,
                    message,
                },
                ...toasts,
            ];
        });

        // "alert" (assertive) for error/warning so it interrupts and is heard without the user
        // having to Tab to it; "status" (polite) for info/success so routine confirmations don't
        // rudely cut off whatever the user is currently doing — same politeness the visual toast
        // used to expose via its own (unreliable) role, now owned solely by the announcer.
        const politeness: AriaLivePoliteness = type === 'error' || type === 'warning' ? 'assertive' : 'polite';
        const announcement: string = [ title, message ]
            .filter((text: string): boolean => text.trim().length > 0)
            .join(' ');
        void this.liveAnnouncer.announce(announcement, politeness);
    }

    /**
     * Remove a toast by ID.
     * @param id The ID of the toast.
     * @private
     */
    private removeById(id: string): void {
        this.toasts.update((toasts: ToastConfiguration[]): ToastConfiguration[] => {
            return toasts.filter((toast: ToastConfiguration): boolean => {
                if (toast.id === id) {
                    clearTimeout(toast.timeoutId);
                    return false;
                }
                return true;
            });
        });
    }
}